import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@material-ui/core";
import {
	Add,
	Delete,
	PhotoCamera,
	Save,
} from "@material-ui/icons";
import requests, { getCsrf } from "../../../Services/HttpServices";
import { readURL } from "../../FormComponents/FileNamesComponent";
import "./CamereArdentiSelect.css";

const smallProcediButtonStyle = {
	minWidth     : 178,
	height       : 36,
	padding      : "0 16px",
	marginLeft   : 0,
	fontSize     : 14,
	whiteSpace   : "nowrap",
	borderRadius : 2,
};

const closeProcediButtonStyle = {
	minWidth     : 120,
	height       : 42,
	padding      : "0 18px",
	marginLeft   : 0,
	fontSize     : 15,
	borderRadius : 2,
};

const defaultExpirationDate = () => {
	const date = new Date();
	date.setDate(date.getDate() + 7);
	date.setHours(23, 59, 0, 0);
	return toDatabaseDateTime(date);
};

const padDatePart = (value) => `${value}`.padStart(2, "0");

const toDatabaseDateTime = (date) => {
	const year = date.getFullYear();
	const month = padDatePart(date.getMonth() + 1);
	const day = padDatePart(date.getDate());
	const hours = padDatePart(date.getHours());
	const minutes = padDatePart(date.getMinutes());
	return `${year}-${month}-${day} ${hours}:${minutes}:00`;
};

const toDateTimeInputValue = (value) => {
	const dateTime = `${value || ""}`.trim();
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateTime)) {
		return `${dateTime}T23:59`;
	}

	const match = dateTime.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2})?$/);
	return match ? `${match[1]}T${match[2]}` : "";
};

const fromDateTimeInputValue = (value) => {
	const dateTime = `${value || ""}`.trim();
	if (!dateTime) return "";

	const normalized = dateTime.replace("T", " ");
	return normalized.length === 16 ? `${normalized}:00` : normalized;
};

const emptyDraft = {
	nome    : "",
	prezzo  : "",
	file    : null,
	preview : "",
};

const getGiftId = (gift) => `${gift.idDono || gift.ID || ""}`;

const formatPrice = (value) => {
	const parsed = parseFloat(value || 0);
	return `${(Number.isNaN(parsed) ? 0 : parsed).toFixed(2).replace(".", ",")}€`;
};

export default function FuneralGiftsSelect({ deceasedID }) {
	const [gifts, setGifts]                   = useState([]);
	const [enabledMap, setEnabledMap]         = useState({});
	const [loading, setLoading]               = useState(false);
	const [error, setError]                   = useState("");
	const [popupOpen, setPopupOpen]           = useState(false);
	const [popupSaving, setPopupSaving]       = useState(false);
	const [popupError, setPopupError]         = useState("");
	const [draft, setDraft]                   = useState(emptyDraft);

	const filteredEnabledMap = useMemo(() => {
		const map = {};
		Object.keys(enabledMap || {}).forEach((idDono) => {
			if (enabledMap[idDono]) {
				map[idDono] = enabledMap[idDono];
			}
		});
		return map;
	}, [enabledMap]);

	const applyResponse = useCallback((response) => {
		if (response?.response === "success") {
			setGifts(response.data?.gifts || []);
			setEnabledMap(response.data?.enabledMap || {});
			setError("");
			setPopupError("");
			return true;
		}

		const message = response?.message || "Doni Funerari non disponibili";
		setError(message);
		setPopupError(message);
		return false;
	}, []);

	const loadGifts = useCallback(() => {
		const query = deceasedID ? `?deceasedID=${encodeURIComponent(deceasedID)}` : "";
		setLoading(true);
		requests("get", "funeralGiftPartnerList", query)
			.then((result) => {
				applyResponse(result?.data);
				setLoading(false);
			})
			.catch(() => {
				setError("Doni Funerari non disponibili");
				setLoading(false);
			});
	}, [applyResponse, deceasedID]);

	useEffect(() => loadGifts(), [loadGifts]);

	const setGiftEnabled = (gift, enabled) => {
		const idDono = getGiftId(gift);
		const nextMap = { ...enabledMap };
		if (enabled) {
			nextMap[idDono] = nextMap[idDono] || defaultExpirationDate();
		} else {
			delete nextMap[idDono];
		}
		setEnabledMap(nextMap);
	};

	const setGiftExpiration = (gift, value) => {
		const idDono = getGiftId(gift);
		setEnabledMap({ ...enabledMap, [idDono]: value });
	};

	const updateGift = (idDono, property, value) => {
		setGifts((current) => current.map((gift) => (
			getGiftId(gift) === `${idDono}` ? { ...gift, [property]: value } : gift
		)));
	};

	const setGiftFile = (idDono, file) => {
		if (!file) return;
		readURL(file).then((preview) => {
			updateGift(idDono, "file", file);
			updateGift(idDono, "preview", preview);
		});
	};

	const setDraftFile = (file) => {
		if (!file) return;
		readURL(file).then((preview) => {
			setDraft((current) => ({ ...current, file, preview }));
		});
	};

	const saveGift = (gift, isDraft = false) => {
		const nome = (gift.nome || "").trim();
		const prezzo = parseFloat(`${gift.prezzo || ""}`.replace(",", "."));
		if (!nome || Number.isNaN(prezzo) || prezzo <= 0) {
			setPopupError("Inserisci nome e prezzo maggiori di zero.");
			return;
		}

		setPopupSaving(true);
		setPopupError("");
		getCsrf().then((csrf) => {
			const formData = new FormData();
			formData.append("csrf_token", csrf);
			formData.append("idDono", isDraft ? "" : getGiftId(gift));
			formData.append("nome", nome);
			formData.append("prezzo", prezzo.toFixed(2));
			if (gift.file) formData.append("immagine", gift.file);

			requests("sendform", "funeralGiftSave", formData)
				.then((response) => {
					if (response?.response === "success") {
						setGifts(response.data?.gifts || []);
						setDraft(emptyDraft);
						setPopupError("");
					} else {
						setPopupError(response?.message || "Errore durante il salvataggio.");
					}
					setPopupSaving(false);
				})
				.catch(() => {
					setPopupError("Errore durante il salvataggio.");
					setPopupSaving(false);
				});
		});
	};

	const deleteGift = (gift) => {
		const idDono = getGiftId(gift);
		setPopupSaving(true);
		requests("post", "funeralGiftDelete", { idDono })
			.then((response) => {
				if (response?.response === "success") {
					setGifts(response.data?.gifts || []);
					const nextMap = { ...enabledMap };
					delete nextMap[idDono];
					setEnabledMap(nextMap);
					setPopupError("");
				} else {
					setPopupError(response?.message || "Errore durante l'eliminazione.");
				}
				setPopupSaving(false);
			})
			.catch(() => {
				setPopupError("Errore durante l'eliminazione.");
				setPopupSaving(false);
			});
	};

	return (
		<div className="funeral-gifts-partner-section">
			<span className="death-data" style={{ display: "block", textAlign: "left" }}>
				Doni Funerari
			</span>
			<input type="hidden" name="doniFunerari" value={JSON.stringify(filteredEnabledMap)} />
			<div className="funeral-gifts-partner-header">
				<Typography variant="body2" style={{ color: "#555", textAlign: "left" }}>
					Abilita i doni funerari acquistabili per questo defunto e imposta la scadenza.
				</Typography>
				<button
					type="button"
					className="activate-submit"
					onClick={() => setPopupOpen(true)}
					style={smallProcediButtonStyle}
				>
					Gestisci Doni Funerari
				</button>
			</div>

			{loading && <Typography variant="body2">Caricamento doni...</Typography>}
			{error && <Typography variant="body2" style={{ color: "#b00020" }}>{error}</Typography>}
			{!loading && gifts.length === 0 && !error && (
				<Typography variant="body2" style={{ color: "#555" }}>
					Nessun dono configurato.
				</Typography>
			)}

			<div className="funeral-gifts-enabled-list">
				{gifts.map((gift) => {
					const idDono = getGiftId(gift);
					const checked = !!enabledMap[idDono];
					return (
						<div className="funeral-gifts-enabled-row" key={idDono}>
							<Checkbox
								color="primary"
								checked={checked}
								onChange={(event) => setGiftEnabled(gift, event.target.checked)}
							/>
							{gift.imageUrl ? (
								<img src={gift.imageUrl} alt={gift.nome} />
							) : (
								<div className="funeral-gifts-image-placeholder" />
							)}
							<div className="funeral-gifts-enabled-info">
								<Typography>{gift.nome}</Typography>
								<Typography variant="body2">{formatPrice(gift.prezzo)}</Typography>
							</div>
							<TextField
								type="datetime-local"
								label="Attivo fino al"
								value={toDateTimeInputValue(enabledMap[idDono])}
								onChange={(event) => setGiftExpiration(gift, fromDateTimeInputValue(event.target.value))}
								disabled={!checked}
								InputLabelProps={{ shrink: true }}
							/>
						</div>
					);
				})}
			</div>

			<Dialog open={popupOpen} onClose={() => setPopupOpen(false)} fullWidth maxWidth="md">
				<DialogTitle className="camere-ardenti-dialog-title">Gestisci Doni Funerari</DialogTitle>
				<DialogContent>
					{popupError && (
						<Typography variant="body2" style={{ color: "#b00020", marginBottom: 14 }}>
							{popupError}
						</Typography>
					)}
					<div className="funeral-gifts-manager-table">
						{gifts.map((gift) => {
							const idDono = getGiftId(gift);
							const inputId = `funeral-gift-image-${idDono}`;
							return (
								<div className="funeral-gifts-manager-row" key={idDono}>
									{gift.preview || gift.imageUrl ? (
										<img src={gift.preview || gift.imageUrl} alt={gift.nome} />
									) : (
										<div className="funeral-gifts-image-placeholder" />
									)}
									<TextField
										label="Nome"
										value={gift.nome || ""}
										onChange={(event) => updateGift(idDono, "nome", event.target.value)}
									/>
									<TextField
										label="Prezzo"
										type="number"
										value={gift.prezzo || ""}
										onChange={(event) => updateGift(idDono, "prezzo", event.target.value)}
									/>
									<input
										id={inputId}
										type="file"
										accept="image/*"
										style={{ display: "none" }}
										onChange={(event) => setGiftFile(idDono, event.target.files?.[0])}
									/>
									<Button component="label" htmlFor={inputId} disabled={popupSaving}>
										<PhotoCamera />
									</Button>
									<Button onClick={() => saveGift(gift)} disabled={popupSaving}>
										<Save />
									</Button>
									<Button onClick={() => deleteGift(gift)} disabled={popupSaving}>
										<Delete />
									</Button>
								</div>
							);
						})}
						<div className="funeral-gifts-manager-row new-row">
							{draft.preview ? (
								<img src={draft.preview} alt="Nuovo dono" />
							) : (
								<div className="funeral-gifts-image-placeholder" />
							)}
							<TextField
								label="Nome"
								value={draft.nome}
								onChange={(event) => setDraft({ ...draft, nome: event.target.value })}
							/>
							<TextField
								label="Prezzo"
								type="number"
								value={draft.prezzo}
								onChange={(event) => setDraft({ ...draft, prezzo: event.target.value })}
							/>
							<input
								id="funeral-gift-image-new"
								type="file"
								accept="image/*"
								style={{ display: "none" }}
								onChange={(event) => setDraftFile(event.target.files?.[0])}
							/>
							<Button component="label" htmlFor="funeral-gift-image-new" disabled={popupSaving}>
								<PhotoCamera />
							</Button>
							<Button onClick={() => saveGift(draft, true)} disabled={popupSaving}>
								<Add />
							</Button>
						</div>
					</div>
				</DialogContent>
				<DialogActions>
					<button
						type="button"
						className="activate-submit camere-ardenti-dialog-close"
						onClick={() => {
							setPopupOpen(false);
							loadGifts();
						}}
						style={closeProcediButtonStyle}
					>
						Chiudi
					</button>
				</DialogActions>
			</Dialog>
		</div>
	);
}